import { useMemo } from 'react';
import cc from 'currency-codes';

/**
 * Currency picker — native `<select>` populated from `currency-codes`
 * (ISO 4217, 179 entries). Mirror of CountryPicker so callers can swap
 * a free-text "type the code" input for a real dropdown.
 *
 * Each option renders as `JPY — Japanese Yen (¥)`. The symbol comes
 * from the browser's `Intl.NumberFormat` so we don't ship a symbol
 * dictionary — for currencies the runtime doesn't recognize, the
 * picker falls back to just code + name.
 *
 * A short list of finance-relevant currencies bubbles to the top so
 * the most common picks (USD/EUR/JPY/GBP/CNY/KRW/INR/BRL/CAD/AUD/SGD/HKD/CHF)
 * are immediately reachable; the rest are alphabetical.
 *
 * Why a native <select> vs custom dropdown: same reasoning as
 * CountryPicker — typeahead by first letter, keyboard + a11y for free,
 * matches the rest of the app's input styling.
 */
export function CurrencyPicker({
  value,
  onChange,
  className,
  id,
  ariaLabel,
  disabled,
}: {
  value: string;                       // ISO 4217 3-letter, e.g. "USD"
  onChange: (code: string) => void;
  className?: string;
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
}) {
  const options = useMemo(() => buildOptions(), []);

  return (
    <select
      id={id}
      value={value.toUpperCase()}
      onChange={(e) => onChange(e.target.value.toUpperCase())}
      disabled={disabled}
      aria-label={ariaLabel}
      className={
        className ??
        'num w-full rounded-md border border-border bg-white px-2 py-1.5 text-xs'
      }
    >
      {options.map((o) =>
        o.divider ? (
          <option key={o.code} value={o.code} disabled>
            ── {o.label} ──
          </option>
        ) : (
          <option key={o.code} value={o.code} title={o.title}>
            {o.label}
          </option>
        ),
      )}
    </select>
  );
}

/**
 * Extract the currency symbol via Intl.NumberFormat. Works for every
 * ISO 4217 currency the browser knows. Falls back to empty string for
 * exotic codes so the picker still shows `XYZ — Currency Name`.
 */
export function symbolFor(code: string, locale = 'en'): string {
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      currencyDisplay: 'narrowSymbol',
    }).formatToParts(0);
    const sym = parts.find((p) => p.type === 'currency')?.value ?? '';
    // Narrow symbol returns the code itself for unknown currencies — strip
    // that to avoid duplication ("USD — US Dollar (USD)").
    return sym === code ? '' : sym;
  } catch {
    return '';
  }
}

interface Option {
  code: string;
  label: string;
  title: string;
  divider?: boolean;
}

/**
 * Currencies that matter for FP&A buyers in our 14 target locales. Pinned
 * to the top of the list to save scrolling.
 */
const PINNED = [
  'USD', 'EUR', 'JPY', 'GBP', 'CNY', 'KRW',
  'INR', 'BRL', 'CAD', 'AUD', 'SGD', 'HKD',
  'CHF', 'MXN', 'IDR', 'BDT', 'RUB', 'AED',
];

function buildOptions(): Option[] {
  const allCodes = (cc.data as Array<{ code: string; currency: string }>).slice();
  const pinnedSet = new Set(PINNED);

  const pinnedRows: Option[] = PINNED
    .map((code) => allCodes.find((c) => c.code === code))
    .filter((c): c is { code: string; currency: string } => Boolean(c))
    .map((c) => toOption(c.code, c.currency));

  const restRows: Option[] = allCodes
    .filter((c) => !pinnedSet.has(c.code))
    .sort((a, b) => a.code.localeCompare(b.code))
    .map((c) => toOption(c.code, c.currency));

  return [
    ...pinnedRows,
    { code: '__divider__', label: 'all currencies', title: '', divider: true },
    ...restRows,
  ];
}

function toOption(code: string, name: string): Option {
  const sym = symbolFor(code);
  const suffix = sym ? ` (${sym})` : '';
  return {
    code,
    label: `${code} — ${name}${suffix}`,
    title: `${name} (ISO ${code})`,
  };
}
